import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { Post } from "../utils/types";
import { collection, getDocs } from "firebase/firestore";
import { Link } from "react-router-dom";
import { extractPlainText } from "../utils/extractPlainText";
import { formatDate } from "../utils/formatDate";
import { getResponsiveImageUrl } from "../utils/getResponsiveImageUrl";
import { categoryNameToColor } from "../utils/categoriesColors";
import ReadingTime from "./ReadingTime";

const SearchBar: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<Post[]>([]);
  const [allPosts, setAllPosts] = useState<Post[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTermRef = useRef(searchTerm);
  const timeoutId = useRef<any>(null);

  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 960);
      setViewportWidth(width);
      if (width > 960) {
        setIsExpanded(false);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const preventScroll = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Mobile: Only prevent scrolling when search is expanded but no results are shown
    if (isExpanded && isMobile && results.length === 0 && searchTerm === "") {
      const scrollY = window.scrollY;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      document.body.style.overflowY = 'scroll';
      document.body.style.height = '100vh';
      document.body.setAttribute('data-search-scroll-y', scrollY.toString());
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      window.scrollTo(0, scrollY);

      window.addEventListener('scroll', preventScroll, { passive: false });
      window.addEventListener('wheel', preventScroll, { passive: false });
      window.addEventListener('touchmove', preventScroll, { passive: false });

      return () => {
        const scrollY = document.body.getAttribute('data-search-scroll-y');
        document.body.style.overflowY = '';
        document.body.style.height = '';
        document.body.style.paddingRight = '';
        document.body.removeAttribute('data-search-scroll-y');
        window.removeEventListener('scroll', preventScroll);
        window.removeEventListener('wheel', preventScroll);
        window.removeEventListener('touchmove', preventScroll);
        if (scrollY) {
          window.scrollTo(0, parseInt(scrollY));
        }
      };
    }
    // Desktop: Prevent scrolling when search results are shown, but allow scrolling within results
    else if (!isMobile && (searchTerm || results.length > 0)) {
      const scrollY = window.scrollY;

      // Keep scrollbar visible to prevent layout shift
      document.body.style.overflowY = 'scroll';
      document.body.style.height = '100vh';
      document.body.setAttribute('data-search-scroll-y', scrollY.toString());
      window.scrollTo(0, scrollY);

      const preventPageScroll = (e: Event) => {
        // Allow scrolling within the search results container
        if (searchResultsRef.current && e.target instanceof Node) {
          if (searchResultsRef.current.contains(e.target)) {
            return; // Allow scrolling within search results
          }
        }
        // Prevent scrolling on the page body
        e.preventDefault();
        e.stopPropagation();
        return false;
      };

      window.addEventListener('scroll', preventPageScroll, { passive: false });
      window.addEventListener('wheel', preventPageScroll, { passive: false });

      return () => {
        const scrollY = document.body.getAttribute('data-search-scroll-y');
        document.body.style.overflowY = '';
        document.body.style.height = '';
        document.body.removeAttribute('data-search-scroll-y');
        window.removeEventListener('scroll', preventPageScroll);
        window.removeEventListener('wheel', preventPageScroll);
        if (scrollY) {
          window.scrollTo(0, parseInt(scrollY));
        }
      };
    } else {
      // Restore scrolling when search is closed or no results
      document.body.style.overflowY = '';
      document.body.style.height = '';
      document.body.style.paddingRight = '';
      document.body.removeAttribute('data-search-scroll-y');
    }
  }, [isExpanded, isMobile, results.length, searchTerm]);

  const clearSearch = React.useCallback(() => {
    setSearchTerm("");
    setResults([]);
    if (isMobile) {
      setIsExpanded(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const fetchPosts = async () => {
      const snapshot = await getDocs(collection(db, "posts"));
      const posts: Post[] = snapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() } as Post)
      );
      setAllPosts(posts);
    };

    fetchPosts();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        if (searchTermRef.current === "" && isMobile) {
          setIsExpanded(false);
        } else if (searchTermRef.current !== "") {
          clearSearch();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile, clearSearch]);

  useEffect(() => {
    if (isExpanded && isMobile && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isExpanded, isMobile]);

  const handleSearch = (term: string) => {
    if (term) {
      const lowercaseTerm = term.toLowerCase();
      const filtered = allPosts.filter((post) =>
        post.title.toLowerCase().includes(lowercaseTerm)
      );
      setResults(filtered);
    } else {
      setResults([]);
    }
  };

  const handleIconClick = () => {
    if (isMobile) {
      if (isExpanded) {
        setIsExpanded(false);
        setSearchTerm("");
        setResults([]);
      } else {
        setIsExpanded(true);
      }
    }
  };

  return (
    <div className={`search-container ${isExpanded ? "expanded" : ""}`} ref={searchContainerRef}>
      {isMobile && !isExpanded && (
        <button
          className="search-icon-button"
          onClick={handleIconClick}
          aria-label="Open search"
          type="button"
        >
          <span className="material-symbols-outlined notranslate">search</span>
        </button>
      )}
      {isMobile && isExpanded && (
        <button
          className="search-close-button"
          onClick={handleIconClick}
          aria-label="Close search"
          type="button"
        >
          <span className="material-symbols-outlined notranslate">close</span>
        </button>
      )}
      <input
        ref={searchInputRef}
        className={`search-input ${isMobile && !isExpanded ? "hidden" : ""}`}
        id="search-input"
        type="text"
        value={searchTerm}
        placeholder="Search..."
        aria-label="Search posts"
        onChange={(e) => {
          const newTerm = e.target.value;
          setSearchTerm(newTerm);
          if (timeoutId.current) {
            clearTimeout(timeoutId.current);
          }
          timeoutId.current = setTimeout(() => {
            handleSearch(newTerm);
          }, 500);
        }}
      />
      {(searchTerm || results.length > 0) && (
        <div className="search-results" ref={searchResultsRef}>
          {results.length > 0
            ? results.map((post, index) => {
              const imageUrl = getResponsiveImageUrl(post, "card", viewportWidth);
              const excerpt = extractPlainText(post.content, 120);
              const categoryColor = post.category
                ? categoryNameToColor(post.category)
                : "#666";
              const publishedDate = post.publishedAt
                ? formatDate(post.publishedAt.toDate())
                : "";

              return (
                <Link
                  key={post.id}
                  to={`/post/${post.id}`}
                  onClick={clearSearch}
                  className="search-result-item animate__animated animate__fadeIn"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="search-result-card">
                    {imageUrl && (
                      <div className="search-result-image">
                        <img src={imageUrl} alt={post.title || "Post image"} />
                      </div>
                    )}
                    <div className="search-result-content">
                      <div className="search-result-header">
                        {post.category && (
                          <span
                            className="search-result-category"
                            style={{
                              backgroundColor: categoryColor,
                              color: "#fff",
                            }}
                          >
                            {post.category}
                          </span>
                        )}
                        {publishedDate && (
                          <span className="search-result-date">
                            {publishedDate}
                          </span>
                        )}
                      </div>
                      <h3 className="search-result-title">{post.title}</h3>
                      {excerpt && (
                        <p className="search-result-excerpt">{excerpt}</p>
                      )}
                      <div className="search-result-meta">
                        {post.viewCount !== undefined && (
                          <span className="search-result-views">
                            <span className="material-symbols-outlined">
                              visibility
                            </span>
                            {post.viewCount}
                          </span>
                        )}
                        {post.content && (
                          <ReadingTime
                            content={
                              typeof post.content === "string"
                                ? post.content
                                : extractPlainText(post.content, 10000)
                            }
                          />
                        )}
                        {post.commentCount > 0 && (
                          <span className="search-result-comments">
                            <span className="material-symbols-outlined">
                              comment
                            </span>
                            {post.commentCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })
            : searchTerm && (
              <div className="no-results animate__animated animate__fadeIn">
                <span className="material-symbols-outlined">search_off</span>
                <p>No results found for "{searchTerm}"</p>
                <p className="no-results-hint">
                  Try different keywords or browse the Kraffcho.DEV categories above.
                </p>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
